import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import { getTypeIcon } from '@atlassian/people-teams-ui';

import { prefixWithContextEntryPoint } from '../../../common/utils/url';
import { useNavigationState } from '../store/main';
import { type AppConfigProviderProps, type AppConfigProviderType, type Flag } from '../types';
const error = new Error('AppConfig is not setup in RSS');

const defaultValue = {
	push: () => {
		throw error;
	},
	replace: () => {
		throw error;
	},
	addFlag: () => {
		throw error;
	},
};

const AppConfigContext = React.createContext<AppConfigProviderType>(defaultValue);

export function AppConfigProvider({
	children,
	push: pushProp,
	replace: replaceProp,
	addFlag: addFlagProp,
}: PropsWithChildren<AppConfigProviderProps>) {
	const { contextEntryPoint } = useNavigationState();

	const addFlag = useCallback(
		(props: Flag) => {
			const { type, ...rest } = props;

			addFlagProp({
				...rest,
				id: rest.id ? `${rest.id}.${Date.now()}` : Date.now(),
				type,
				appearance: 'normal',
				icon: getTypeIcon(type),
			});
		},
		[addFlagProp],
	);

	const push = useCallback(
		(path: string) => {
			const finalPath = prefixWithContextEntryPoint(path, contextEntryPoint);
			return pushProp(finalPath);
		},
		[pushProp, contextEntryPoint],
	);

	const replace = useCallback(
		(path: string) => {
			const finalPath = prefixWithContextEntryPoint(path, contextEntryPoint);

			return replaceProp(finalPath);
		},
		[contextEntryPoint, replaceProp],
	);

	const configValue = useMemo(
		() => ({
			addFlag,
			push,
			replace,
		}),
		[addFlag, push, replace],
	);

	return <AppConfigContext.Provider value={configValue}>{children}</AppConfigContext.Provider>;
}

const useAppConfigProvider = () => React.useContext(AppConfigContext);

export const useNavigationActions = () => {
	const { push, replace } = useAppConfigProvider();
	return { push, replace };
};

export const useAddFlag = () => {
	const { addFlag } = useAppConfigProvider();
	return addFlag;
};
